import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BaseDirective } from '@egym/ngx-adm-int';
import { Gym } from 'src/app/model/gym';
import { GymUpdate } from 'src/app/model/gym-update';
import { GymFinderService } from 'src/app/service/gymfinder.service';
import { ImageUtil } from 'src/app/util/image-util';

@Component({
  selector: 'gymfinder-gym-image',
  templateUrl: './gym-image.component.html',
  styleUrls: ['./gym-image.component.scss'],
  standalone: false,
})
export class GymImageComponent extends BaseDirective<void> {
  @Input()
  gym: GymUpdate;

  @Input()
  updatingImages: boolean;

  @Output()
  gymChange = new EventEmitter<Gym>();

  @Output()
  updatingImagesChange = new EventEmitter<boolean>();

  imagesToAdd: File[] = [];

  imagesToDelete: string[] = [];

  allowedImageTypes = ['image/png', 'image/jpg', 'image/jpeg'];

  maxImageWidth = 4000;

  constructor(
    snackBar: MatSnackBar,
    private gymFinderService: GymFinderService
  ) {
    super(snackBar);
  }

  public selectMainPicture(imageId: string) {
    this.gym.mainImageId = imageId;
    this.gymChange.emit(this.gym);
  }

  public deleteExistingImage(existingImageId: string) {
    this.imagesToDelete.push(existingImageId);
    this.updatingImagesChange.emit(true);

    this.gymFinderService
      .deleteImages(this.gym.gymId ?? 0, this.imagesToDelete)
      .subscribe({
        next: () => {
          this.openSnackBar('Image deleted successfully');
          this.gym.gymImages = this.gym.gymImages.filter(
            ({ imageId }) => this.imagesToDelete[0] !== imageId
          );
          if (this.gym.mainImageId === this.imagesToDelete[0]) {
            this.selectMainPicture(this.gym.gymImages[0].imageId);
          }
          this.gymChange.emit(this.gym);
          this.imagesToDelete = [];
          this.updatingImagesChange.emit(false);
        },
        error: (e: HttpErrorResponse) => {
          this.error = e;
          this.openSnackBar('Could not delete image, please try again!');
          this.imagesToDelete = [];
          this.updatingImagesChange.emit(false);
        },
      });
  }

  public onFileSelected(event: Event) {
    const target = event.target as HTMLInputElement;
    if (target.files) {
      this.addFiles(Array.from(target.files));
      // This enables upload of the same files again
      target.value = '';
    }
  }

  public onFileDropped(files: FileList) {
    this.addFiles(
      Array.from(files).filter(file =>
        this.allowedImageTypes.includes(file.type)
      )
    );
  }

  private addFiles(files: File[]) {
    this.updatingImagesChange.emit(true);

    const totalFiles = files.length;
    let remainingFiles = totalFiles;
    let invalidFilesCount = 0;

    this.imagesToAdd = [];

    files.forEach(file => {
      const objectUrl = URL.createObjectURL(file);
      const img = new Image();

      img.onload = async () => {
        const ratio = img.width / img.height;
        URL.revokeObjectURL(objectUrl);

        if (ratio >= 1) {
          if (img.width > this.maxImageWidth) {
            const resizedBlob = await ImageUtil.resizeImageToBlob(
              img,
              this.maxImageWidth,
              file.type
            );

            if (resizedBlob) {
              const resizedFile = new File([resizedBlob], file.name, {
                type: file.type,
              });
              this.imagesToAdd.push(resizedFile);
            } else {
              this.openSnackBar(`Could not resize image "${file.name}"`);
              this.imagesToAdd.push(file); // fallback to original
            }
          } else {
            this.imagesToAdd.push(file);
          }
        } else {
          invalidFilesCount++;
          this.openSnackBar(
            `Image "${file.name}" is not in landscape format and was not uploaded.`
          );
        }

        remainingFiles--;
        if (remainingFiles === 0) {
          if (this.imagesToAdd.length > 0) {
            this.uploadImages(totalFiles, invalidFilesCount);
          } else {
            this.updatingImagesChange.emit(false);
          }
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        this.openSnackBar(`Could not load image "${file.name}"`);
        remainingFiles--;
        if (remainingFiles === 0 && this.imagesToAdd.length === 0) {
          this.updatingImagesChange.emit(false);
        }
      };

      img.src = objectUrl;
    });
  }

  private uploadImages(filesCount: number, invalidFilesCount: number) {
    this.updatingImagesChange.emit(true);

    this.gymFinderService
      .addImages(this.gym.gymId ?? 0, this.imagesToAdd)
      .subscribe({
        next: images => {
          this.gym.gymImages.push(...images);
          this.gymChange.emit(this.gym);
          this.imagesToAdd = [];

          this.openSnackBar(
            `${
              filesCount - invalidFilesCount
            } of ${filesCount} images added successfully${
              invalidFilesCount
                ? ' (They need to be in landscape format).'
                : '!'
            }`
          );

          this.updatingImagesChange.emit(false);
        },
        error: (e: HttpErrorResponse) => {
          this.error = e;
          this.openSnackBar('Could not upload images, please try again!');
          this.updatingImagesChange.emit(false);
          this.imagesToAdd = [];
        },
      });
  }
}
